/**
 * SWARM FORMATIONS — what the Mother chooses to become.
 * Spec: docs/BOSS-SWARM-MOTHER.md, "The formation library".
 *
 * A formation is a PURE FUNCTION from slot index to local offset. That is the
 * whole interface, and it is why the boss can afford to be spectacular: the
 * body and the morph machinery are the one-time expense, after which each new
 * shape costs about fifteen lines.
 *
 * TWO RULES EVERY FORMATION MUST FOLLOW:
 *
 *   1. PURE AND STABLE. Same slot, same count, same offset, every call. The
 *      body re-evaluates stations when slots regenerate, so a formation that
 *      drifted between calls would tear the shape apart.
 *   2. SLOT ORDER IS SPATIAL ORDER, ROUGHLY. Slot n and slot n+1 should end up
 *      near each other in most formations. Holes are per-slot and survive
 *      morphs, so a corridor cut through the wall has to remain a recognisable
 *      wound in the ring — if consecutive slots scattered, damage would read
 *      as noise instead of as a wound.
 *
 * Offsets are in body-local units. The playfield is about 36 wide, so a
 * formation spanning +/-13 crowds it without sealing it.
 */

import { silhouetteStation, type SilhouetteName } from './SwarmSilhouettes'
export type { Station } from './SwarmSilhouettes'
import type { Station } from './SwarmSilhouettes'

/**
 * The library, sequenced as an ARC rather than shuffled.
 *
 * The Mother is learning during the fight, and what she chooses to become is
 * the story: raw mass -> natural forms -> mimicry of the player -> her true
 * geometry -> desperation. The encounter walks this order; nothing here
 * enforces it, but shuffling it throws away the only narrative the boss has.
 */
export type FormationName =
  | 'wall'
  | 'funnel'
  | 'shoal'
  | 'ring'
  | 'eagle'
  | 'hand'
  | 'fighter'
  | 'helix'
  | 'lattice'
  | 'collapse'

/** Golden angle. Gives even packing without clumping at the centre. */
const PHI = 2.39996323

/**
 * WALL — a slab across the top of the arena.
 *
 * Phase 1. Teaches the verb: shoot to make a hole. A grid is the right shape
 * for that lesson precisely because it is featureless — any hole the player
 * makes is one they made, not one the formation gave them.
 */
function wall(slot: number, total: number): Station {
  const cols = 48
  const rows = Math.ceil(total / cols)
  const col = slot % cols
  const row = Math.floor(slot / cols)
  return {
    x: (col - (cols - 1) / 2) * 0.56,
    y: (row - (rows - 1) / 2) * 0.44,
  }
}

/**
 * RING — concentric rotating bands with an open centre.
 *
 * Phase 2. The hole is GIVEN, and then it moves. The player who learned to cut
 * their own corridor now has to learn that a corridor they did not cut cannot
 * be relied on.
 *
 * Flattened vertically because the playfield is much wider than it is tall; a
 * true circle would hang off the top and bottom of the screen.
 */
function ring(slot: number, total: number): Station {
  const bands = 12
  const per = Math.ceil(total / bands)
  const band = Math.floor(slot / per)
  const i = slot % per
  const radius = 4.6 + band * 0.66
  // Offset each band so spokes do not line up into radial seams.
  const a = (i / per) * Math.PI * 2 + band * 0.31
  return {
    x: Math.cos(a) * radius,
    y: Math.sin(a) * radius * 0.54,
  }
}

/**
 * COLLAPSE — the mass condensed onto the core.
 *
 * Phase 3. This sounds easier than the wall and is not: the tunnel is at its
 * shortest but the density is at its highest, so the player has to cut through
 * more drones per unit of distance than at any other point in the fight.
 *
 * Phyllotaxis packing — the same spiral a sunflower uses — because a grid at
 * this density reads as a crystal and the collapse should read as a body
 * pulling itself in.
 */
function collapse(slot: number, total: number): Station {
  const a = slot * PHI
  const r = Math.sqrt((slot + 0.5) / total) * 6.6
  return {
    x: Math.cos(a) * r,
    y: Math.sin(a) * r * 0.72,
  }
}

/**
 * FUNNEL — a rotating vortex touching down on the fields.
 *
 * Phase 1. A tornado over Virginia farmland reads as WEATHER, not machinery,
 * which is the first hint that this thing is not a vehicle. Radius widens with
 * height and the whole column twists, so the safe ground moves predictably.
 */
function funnel(slot: number, total: number): Station {
  const t = slot / total
  // Narrow at the bottom, flaring at the top.
  const height = -5.6 + t * 11.4
  const radius = 1.1 + Math.pow(t, 1.5) * 11.5
  // Multiple turns up the column, so it reads as a spiral rather than a cone.
  const a = t * Math.PI * 2 * 9 + slot * 0.11
  return { x: Math.cos(a) * radius, y: height + Math.sin(a) * radius * 0.14 }
}

/**
 * SHOAL — dispersed into a fish-school.
 *
 * Phase 1-2. The loosest formation in the library and the first time the
 * player's fire feels REFUSED: there is no dense mass to cut, so a corridor
 * cannot be opened and sustained fire simply thins the school.
 *
 * Built as overlapping bands at varying phase so it flows rather than sits.
 */
function shoal(slot: number, total: number): Station {
  const bands = 9
  const per = Math.ceil(total / bands)
  const band = Math.floor(slot / per)
  const i = slot % per
  const t = i / per
  // Each band is a shallow arc, offset and scaled so they interleave.
  const sweep = (t - 0.5) * 26
  const droop = Math.cos((t - 0.5) * Math.PI) * 1.5
  return {
    x: sweep + Math.sin(band * 1.7) * 1.8,
    y: -4.4 + band * 1.05 + droop + Math.sin(t * 9 + band) * 0.5,
  }
}

/**
 * HELIX — two counter-rotating spirals.
 *
 * Phase 3. Corridors that sweep predictably. After the chaos of the mimicry
 * shapes this is legible density: the gaps are wide and obvious, but they
 * move, so holding one costs constant attention.
 */
function helix(slot: number, total: number): Station {
  const strand = slot % 2 === 0 ? 1 : -1
  const t = Math.floor(slot / 2) / (total / 2)
  const a = t * Math.PI * 2 * 3 * strand
  return {
    x: (t - 0.5) * 26,
    y: Math.sin(a) * 5.2,
  }
}

/**
 * LATTICE — a rigid crystalline grid, perfectly regular and motionless.
 *
 * Phase 3. She stops pretending. Every other formation imitates something
 * living; this is what the thing actually is, and its total regularity after
 * the eagle and the fighter is the point. Deliberately the least organic shape
 * in the library.
 */
function lattice(slot: number, total: number): Station {
  const cols = 40
  const rows = Math.ceil(total / cols)
  const col = slot % cols
  const row = Math.floor(slot / cols)
  // Offset alternate rows into a triangular lattice — a square grid reads as
  // graph paper, a triangular one reads as crystal.
  const stagger = (row % 2) * 0.34
  return {
    x: (col - (cols - 1) / 2) * 0.68 + stagger,
    y: (row - (rows - 1) / 2) * 0.52,
  }
}

/** Silhouette formations delegate to the baked point sets. */
const eagle = (slot: number, total: number) => silhouetteStation('eagle', slot, total)
const hand = (slot: number, total: number) => silhouetteStation('hand', slot, total)
const fighter = (slot: number, total: number) => silhouetteStation('fighter', slot, total)

export const FORMATIONS: Record<FormationName, (slot: number, total: number) => Station> = {
  wall,
  funnel,
  shoal,
  ring,
  eagle,
  hand,
  fighter,
  helix,
  lattice,
  collapse,
}

/** The authored order. The encounter walks this; see FormationName. */
export const FORMATION_ARC: FormationName[] = [
  'wall',
  'funnel',
  'shoal',
  'ring',
  'eagle',
  'hand',
  'fighter',
  'helix',
  'lattice',
  'collapse',
]

export type { SilhouetteName }
